import styled from '@emotion/styled'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { routeName } from '~/@common/constants'
import authService from '~/@common/services/auth.service'
import { commonRepo } from '~/@common/services/repo.service'
import { colors, textEllipsis, typo } from '~/@common/styles'
import { Icon } from '~/@common/ui/Icon/Icon'
import type { IconNames } from '~/@common/ui/Icon/iconNames.type.ts'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'
import type { EventSchema } from '~/@common/utils/trackings/schema/event.schema'

import { QRScanButton } from '../GNB/QRScanButton'

type NavItem = {
  to: string
  label: string
  iconName: IconNames
  eventName: keyof EventSchema
  end?: boolean
}

type SubNavItem = {
  to: string
  label: string
  iconName: IconNames
  eventName: keyof EventSchema
  shouldShow?: boolean
}

const getNavItems = () =>
  [
    {
      to: getEntrancePage(authService.userType),
      label: '학습 홈',
      iconName: 'icon_home' as const,
      eventName: 'GNB_학습홈탭_Click' as const,
      end: true,
    },
    {
      to: routeName.student.challenge,
      label: '챌린지',
      iconName: 'icon_smile' as const,
      eventName: 'GNB_챌린지탭_Click' as const,
    },
    {
      to: routeName.student.studentWorksheet,
      label: '학습지',
      iconName: 'icon_description_paper' as const,
      eventName: 'GNB_학습지탭_Click' as const,
    },
  ] satisfies NavItem[]

const getSubNavItems = () => {
  const { byFeature } = commonRepo.permission
  return [
    {
      to: routeName.student.studentWorkbook,
      label: '교재',
      iconName: 'icon_book_opened' as const,
      eventName: 'GNB_교재탭_Click' as const,
      shouldShow: byFeature.isRouteAllowed(routeName.student.studentWorkbook),
    },
    {
      to: routeName.student.lectureList,
      label: tJosa('강의'),
      iconName: 'icon_video' as const,
      eventName: 'GNB_강의탭_Click' as const,
      shouldShow:
        (commonRepo.studentAppSetting?.lectureService ?? false) &&
        byFeature.isRouteAllowed(routeName.student.lectureList),
    },
  ] satisfies SubNavItem[]
}

type NavLinkItemProps = {
  item: NavItem
  onClick: () => void
}

const NavLinkItem = ({ item, onClick }: NavLinkItemProps) => {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => clsx('nav-link', isActive && 'active')}
      end={item.end}
      onClick={onClick}
    >
      <Icon name={item.iconName} color={colors.blue.$500} size={20} />
      {item.label}
    </NavLink>
  )
}

type SubNavLinkItemProps = {
  item: SubNavItem
  onClick: () => void
}

const SubNavLinkItem = ({ item, onClick }: SubNavLinkItemProps) => {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => clsx('sub-link', isActive && 'active')}
      onClick={onClick}
    >
      <Icon name={item.iconName} color={colors.blue.$500} size={20} />
      {item.label}
    </NavLink>
  )
}

type MoreNavSectionProps = {
  isMoreOpen: boolean
  onToggleMore: () => void
}

const MoreNavSection = ({ isMoreOpen, onToggleMore }: MoreNavSectionProps) => {
  const subNavItems = getSubNavItems()

  return (
    <NavLink
      className={({ isActive }) => clsx('nav-link', 'more', (isActive || isMoreOpen) && 'active')}
      to={routeName.student.studentWorkbook}
      onClick={onToggleMore}
    >
      더보기
      {isMoreOpen && (
        <>
          {subNavItems
            .filter((item) => item.shouldShow)
            .map((item) => (
              <SubNavLinkItem
                key={item.to}
                item={item}
                onClick={() => mixpanelClient.track(item.eventName)}
              />
            ))}
        </>
      )}
    </NavLink>
  )
}

type UserProfileSectionProps = {
  onClickProfile: () => void
}

const UserProfileSection = ({ onClickProfile }: UserProfileSectionProps) => {
  return (
    <NavLink
      to={routeName.profile}
      className={({ isActive }) => clsx('profile', isActive && 'active')}
      onClick={onClickProfile}
    >
      {({ isActive }) => (
        <>
          <img
            src={s3URL.student(
              isActive ? 'images/img__profile-active.svg' : 'images/img__profile.svg',
            )}
            alt="프로필"
          />
          <div className="profile-name">
            <span className="name">{commonRepo.student?.name}&nbsp;</span>
            <span>학생</span>
          </div>
        </>
      )}
    </NavLink>
  )
}

const checkShouldOpenMore = (pathname: string): boolean => {
  return (
    pathname.includes(routeName.student.studentWorkbook) ||
    pathname.includes(routeName.student.lectureList)
  )
}

const SideGNB = () => {
  const location = useLocation()
  const [isMoreOpen, setIsMoreOpen] = useState(() => checkShouldOpenMore(location.pathname))

  useEffect(() => {
    if (checkShouldOpenMore(location.pathname)) {
      setIsMoreOpen(true)
    }
  }, [location.pathname])

  // 권한 로드 전에는 내부 메뉴는 렌더하지 않는다.
  if (!commonRepo.isPermissionLoaded) {
    return <S.Container />
  }

  const { byFeature } = commonRepo.permission

  const handleClickNavLink = (eventName: keyof EventSchema) => {
    setIsMoreOpen(false)
    mixpanelClient.track(eventName)
  }

  const navItems = getNavItems().filter((item) => byFeature.isRouteAllowed(item.to))
  const isQREnabled = byFeature.isAllowed('QR')

  return (
    <S.Container>
      <div className="logo">
        <img src={commonRepo.academyLogoUrl} alt="로고" />
      </div>
      <div className="nav-wrapper">
        <div className="nav-links">
          {navItems.map((item) => (
            <NavLinkItem
              key={item.to}
              item={item}
              onClick={() => handleClickNavLink(item.eventName)}
            />
          ))}
          <MoreNavSection
            isMoreOpen={isMoreOpen}
            onToggleMore={() => {
              setIsMoreOpen(true)
              mixpanelClient.track('GNB_더보기_Click')
            }}
          />
        </div>
        <div className="user-section">
          {isQREnabled && (
            <QRScanButton
              qrButton={() => (
                <S.QRButtonWrapper>
                  <Icon name="icon_qr_scan" color="var(--Colors-Gray-600)" size={22} />
                  <p>QR 카메라</p>
                </S.QRButtonWrapper>
              )}
            />
          )}
          <UserProfileSection onClickProfile={() => mixpanelClient.track('GNB_내정보버튼_Click')} />
        </div>
      </div>
    </S.Container>
  )
}

export default observer(SideGNB)

const BLUE_ALPHA_005 = 'rgba(57, 95, 226, 0.05)'
const BLUE_ALPHA_010 = 'rgba(57, 95, 226, 0.1)'
const BLUE_ALPHA_020 = 'rgba(57, 95, 226, 0.2)'

const S = {
  Container: styled.header`
    display: flex;
    flex-direction: column;
    width: 198px;
    height: 100vh;
    padding: 54px 24px;
    gap: 40px;

    .logo {
      width: 140px;
      height: 40px;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .nav-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
    }

    .nav-links {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .nav-link {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 8px 14px;
      color: ${colors.gray.$700};
      white-space: nowrap;
      font-weight: 700;
      ${typo.heading03};

      > svg {
        display: none;
      }

      &.active {
        background-color: ${BLUE_ALPHA_005};
        border-radius: var(--Radius-200);
        color: ${colors.blue.$500};

        > svg {
          display: block;
        }
      }

      &.more {
        flex-direction: column;
        gap: 10px;
      }
    }

    .sub-link {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 8px 14px;
      width: 100%;
      background: white;
      color: ${colors.gray.$700};
      white-space: nowrap;
      border: 1px solid transparent;
      border-radius: var(--Radius-300);

      > svg {
        display: none;
      }

      &.active {
        border: 1px solid ${BLUE_ALPHA_010};
        background: ${BLUE_ALPHA_010};
        color: ${colors.blue.$500};

        > svg {
          display: block;
        }
      }
    }

    .user-section {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .profile {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 10px 14px;
      color: ${colors.gray.$700};
      white-space: nowrap;
      font-weight: 700;
      border-radius: 8px;
      height: 52px;
      border: 1px solid ${colors.gray.$300};
      ${typo.heading04};

      &.active {
        border: 1px solid ${BLUE_ALPHA_020};
        background: ${BLUE_ALPHA_005};
        color: var(--Colors-Blue-600);
      }
    }

    .profile-name {
      display: flex;
      width: 100%;
      ${textEllipsis(1)};
    }
  `,
  QRButtonWrapper: styled.div`
    display: flex;
    align-items: center;
    display: flex;
    height: 52px;
    padding: 10px 16px;
    align-items: center;
    gap: 6px;
    align-self: stretch;
    border-radius: 8px;
    border: 1px solid var(--Colors-Gray-300);
    font-weight: 700;
    color: var(--Colors-Gray-700);
    ${typo.heading04};
  `,
}
